import { currentLoggedRelyingParty, currentLoggedUserVar } from '@/shared/api';
import SettingsContext, { type Setting } from '@/shared/lib/context/settingsContext';
import type { RelyingPartySettingType } from '@/shared/lib/types/relyingParty';
import { useReactiveVar } from '@apollo/client';
import type { FC, ReactNode } from 'react';
import React, { useEffect } from 'react';

type SettingsProviderProps = {
  children: ReactNode;
};

// Lives in app rather than beside its context: it reads the logged-in user and relying party
// and parses their settings payloads, which is application composition rather than a shared
// primitive. The context itself stays in shared, where pages and shared hooks can read it.
export const SettingsProvider: FC<SettingsProviderProps> = ({ children }) => {
  const [settings, setSettings] = React.useState<Record<string, Setting>>({});
  const [companySettings, setCompanySettings] = React.useState<RelyingPartySettingType>();
  const currentUser = useReactiveVar(currentLoggedUserVar);

  const currentRelyingParty = useReactiveVar(currentLoggedRelyingParty);

  useEffect(() => {
    if (currentRelyingParty?.settings) {
      try {
        const parsedSettings: RelyingPartySettingType = JSON.parse(currentRelyingParty.settings);
        setCompanySettings(parsedSettings);
      } catch (err) {
        console.error(err);
      }
    }
  }, [currentRelyingParty]);

  useEffect(() => {
    if (currentUser?.settings) {
      parseSettingsJsonString(currentUser.settings);
    }
  }, [currentUser]);

  const parseSettingsJsonString = (jsonStr: string) => {
    if (jsonStr?.length > 0) {
      try {
        const parsedSettings = JSON.parse(jsonStr);
        setSettings(parsedSettings);
      } catch (err) {
        console.error(err);
      }
    }
  };

  return (
    <SettingsContext.Provider
      value={{
        parseSettingsJsonString,
        settings,
        companySettings,
        isSecurityPinSet: currentUser?.isSecurityPinSet ?? false
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export default SettingsProvider;
